import { useEffect, useState } from "react";
import { Target, Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * DailyChallengeCard — daily engagement hook. Server picks one task per day
 * per user; user taps "GO" to be routed to the flow that completes it and
 * earn XP. Progress persists via localStorage so the "check" state survives
 * a refresh even before backend confirms.
 */
export default function DailyChallengeCard({ onNavigate }) {
  const [state, setState] = useState({ loading: true, challenge: null });

  useEffect(() => {
    let cancelled = false;
    api
      .get("/daily/challenge")
      .then((res) => {
        if (!cancelled) setState({ loading: false, challenge: res.data });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, challenge: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const complete = async () => {
    if (!state.challenge || state.challenge.completed) return;
    try {
      const res = await api.post("/daily/challenge/complete");
      setState((prev) => ({
        ...prev,
        challenge: { ...prev.challenge, ...res.data },
      }));
      toast.success(`+${state.challenge.xp || 25} XP · ${state.challenge.title || "Challenge complete"}`);
    } catch {
      toast.error("Couldn't complete challenge. Try again.");
    }
  };

  const go = () => {
    if (!state.challenge) return;
    const route = state.challenge.route;
    // If the challenge points to our current page (e.g. defeat_a_bill → "/"),
    // scroll to the Monthly Battles section instead of doing a no-op nav.
    const samePath = route && route === window.location.pathname;
    if (samePath) {
      const target = document.querySelector('[data-testid="monthly-battles-section"]');
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      // Mark commitment complete — user acknowledged the challenge and was
      // guided to the actionable spot. Real defeat still requires the user
      // to tap Defeat on a specific bill.
      complete();
      return;
    }
    if (route && onNavigate) onNavigate(route);
    if (state.challenge.auto_complete) complete();
  };

  if (state.loading) {
    return (
      <div
        className="rounded-2xl bg-[#0B221D] border border-[#1B4238] p-4 flex items-center gap-3"
        data-testid="daily-challenge-loading"
      >
        <Loader2 className="w-5 h-5 animate-spin text-[#22E07A]" />
        <span className="text-xs font-bold text-[#93B1A8]">Loading challenge…</span>
      </div>
    );
  }

  const c = state.challenge;
  if (!c) return null;

  const pct = Math.min(100, Math.max(0, Math.round((c.progress || 0) * 100 / Math.max(1, c.target || 1))));

  return (
    <div
      className="rounded-2xl bg-[#0B221D] border border-[#1B4238] p-4 relative overflow-hidden"
      data-testid="daily-challenge-card"
    >
      <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#22E07A]/10 blur-2xl" aria-hidden />
      <div className="relative flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[#0F2E27] border border-[#1B4238] flex items-center justify-center flex-shrink-0">
          {c.completed ? (
            <Check className="w-6 h-6 text-[#22E07A]" strokeWidth={3} />
          ) : (
            <Target className="w-6 h-6 text-[#22E07A]" strokeWidth={2.6} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] font-black uppercase tracking-[0.18em] text-[#22E07A]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Daily Challenge
          </p>
          <p
            className="text-sm font-extrabold text-white leading-tight mt-0.5 truncate"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="daily-challenge-title"
          >
            {c.title}
          </p>
          <p className="text-[11px] text-[#93B1A8] mt-0.5 leading-snug line-clamp-2">
            {c.description}
          </p>
          {c.target > 1 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-[#0F2E27] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#22E07A] to-[#7BFFB0] transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-[10px] font-black text-[#93B1A8] tabular-nums">
                {c.progress || 0}/{c.target}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span
            className="text-[10px] font-black uppercase tracking-wider text-[#22E07A]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            +{c.xp || 25} XP
          </span>
          <button
            type="button"
            onClick={c.completed ? undefined : go}
            disabled={c.completed}
            className={`h-8 px-4 rounded-lg text-[11px] font-black uppercase tracking-wider border-2 transition-all ${
              c.completed
                ? "border-[#1B4238] text-[#5F7E76] cursor-default"
                : "border-[#22E07A] text-[#22E07A] hover:bg-[#22E07A] hover:text-[#05160D]"
            }`}
            data-testid="daily-challenge-go"
          >
            {c.completed ? "DONE" : "GO"}
          </button>
        </div>
      </div>
    </div>
  );
}
